import { Modal } from '@razorpay/blade/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { useFormWithProvider } from 'forms';
import { Wizard, WizardStep } from 'forms/Wizard';
import React, { useMemo, useState } from 'react';
import BasicDetailsStep from './BasicDetailsStep';
import { INITIAL_FORM_DATA } from './constants';
import DefineStructureStep from './DefineStructureStep';
import { SalaryStructureFormData } from './types';
import { useQuery } from 'react-query';
import { salaryStructureQueries } from '../constants';
import { GetSalaryStructureGraphQLRequest } from 'api/gql-utils/salaryStructures/types';
import api from 'api';
import { SalaryStructureComponent } from 'components/SalaryStructure/types';
import { parseGetSalaryStructureResponse } from 'components/SalaryStructure/utils';
import { AppError } from 'utils/AppError';
import { ExitConfirmationModal } from './components/ExitConfirmationModal';

type SalaryStructureCreationFlowProps = {
  isOpen: boolean;
  onClose: () => void;
};

const SalaryStructureCreationFlow = ({ isOpen, onClose }: SalaryStructureCreationFlowProps) => {
  const { setFormData, formData } = useFormWithProvider<SalaryStructureFormData>();
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const isCreationTypeFromTemplate = formData.creationType === 'from-existing';
  const { isFetching, refetch, isError, error } = useQuery({
    queryKey: [
      salaryStructureQueries.getSalaryStructure,
      formData.templateId?.[0],
      formData.isEditTemplateMode,
    ],
    queryFn: () => {
      let input: GetSalaryStructureGraphQLRequest;
      const templateId =
        (isCreationTypeFromTemplate || formData.isEditTemplateMode) && formData.templateId?.[0]
          ? formData.templateId[0]
          : undefined;

      input = {
        input: templateId
          ? { id: templateId }
          : {
              type: 'SALARY_STRUCTURE_TYPE_DEFAULT',
            },
      };

      return api.salaryStructures.queries.getSalaryStructure(input);
    },
    select: (response) => {
      const data = parseGetSalaryStructureResponse(response);
      const salaryStructureComponents =
        data.payrollGetSalaryStructure.data.salaryStructureComponents;
      const components = salaryStructureComponents.reduce((acc, component) => {
        acc[component.salaryComponent.id] = component;
        return acc;
      }, {} as Record<string, SalaryStructureComponent>);

      return {
        payrollData: data.payrollGetSalaryStructure.data,
        components,
      };
    },
    onSuccess: (data) => {
      const { components, payrollData } = data;
      setFormData({
        ...formData,
        ...(formData.isEditTemplateMode && formData.templateId?.[0]
          ? {
              name: payrollData?.name,
              description: payrollData?.description,
              type: payrollData?.type,
            }
          : {}),
        templateId: [payrollData?.id],
        components,
        componentIds: payrollData?.salaryStructureComponents.map((component) => component.id),
      });
    },
  });

  const handleAttemptClose = () => {
    setIsExitConfirmationOpen(true);
  };

  const handleConfirmExit = () => {
    setIsExitConfirmationOpen(false);
    setFormData(INITIAL_FORM_DATA);
    onClose();
  };

  const handleCancelExit = () => {
    setIsExitConfirmationOpen(false);
  };

  const onSuccess = () => {
    setFormData(INITIAL_FORM_DATA);
    onClose();
  };

  const BasicDetailsStepComponent = useMemo(
    () => () =>
      (
        <BasicDetailsStep
          isFetchingTemplate={isFetching}
          refetchTemplate={refetch}
          templateError={error as AppError}
        />
      ),
    [isFetching, refetch, isError],
  );

  const DefineStructureStepComponent = useMemo(
    () => () =>
      (
        <DefineStructureStep
          isFetchingTemplate={isFetching}
          refetchTemplate={refetch}
          hasTemplateError={isError}
        />
      ),
    [isFetching, refetch, isError],
  );

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={handleAttemptClose} size="full">
        <WizardInfoProvider
          initialStep={0}
          totalSteps={2}
          onWizardClose={handleAttemptClose}
          onSuccess={onSuccess}>
          <Wizard>
            <WizardStep step={0} component={BasicDetailsStepComponent} />
            <WizardStep step={1} component={DefineStructureStepComponent} />
          </Wizard>
        </WizardInfoProvider>
      </Modal>

      <ExitConfirmationModal
        isOpen={isExitConfirmationOpen}
        onClose={handleCancelExit}
        onConfirmExit={handleConfirmExit}
        isEditMode={formData.isEditTemplateMode}
      />
    </>
  );
};

export default SalaryStructureCreationFlow;
